import { useState, useEffect, useRef } from 'react';
import { Copy, Sparkles } from 'lucide-react';

const genericEmail = {
  to: 'stefan.meier@kontivo.com',
  subject: 'Quick question for Stefan',
  body: `Hi Stefan,

I hope this email finds you well! I came across Kontivo and was really impressed by what you and your team are building in the contract management space.

I'm Alex, co-founder of Revmo. We help B2B companies like Kontivo generate more pipeline and accelerate revenue growth through AI-powered outreach. We've helped companies across SaaS, fintech, logistics, and more achieve 3-5x improvements in their outbound response rates.

I noticed you're the Head of Business Development, so I thought you'd be the right person to connect with. I'd love to learn more about how Kontivo currently handles outbound and show you how we could help you hit your growth targets faster.

Would you be open to a quick 15-minute call this week? I have availability Tuesday at 2pm or Thursday at 10am.

Looking forward to connecting!

Best,
Alex`,
  label: 'Generic cold email',
  sublabel: '"Personalized" with {first_name} and {company}',
};

const hypemaEmail = {
  to: 'stefan.meier@kontivo.com',
  subject: 'Kontivo UK push',
  body: `Hi Stefan,

Noticed you just posted two UK account exec roles — looks like Kontivo is taking the platform into English-speaking markets.

Most DACH teams at this stage hit the same wall: founders writing outreach manually, or blasting templates that tank deliverability in a new market.

One bootstrapped SaaS your size went from 2 to 9 booked meetings/week with individually researched emails.

Worth exploring, or not on your radar?

Best,
Alex`,
  label: 'HyPeMa email',
  sublabel: 'Written for Stefan in 6 seconds',
};

export function EmailComparison() {
  const [active, setActive] = useState<'generic' | 'hypema'>('generic');
  const switched = useRef(false);
  const current = active === 'generic' ? genericEmail : hypemaEmail;

  // Auto-switch once after 2s
  useEffect(() => {
    const id = setTimeout(() => {
      if (!switched.current) {
        switched.current = true;
        setActive('hypema');
      }
    }, 3000);
    return () => clearTimeout(id);
  }, []);

  return (
    <div>
      {/* Toggle */}
      <div className="flex justify-center mb-4">
        <div className="inline-flex rounded-xl bg-base-300/30 p-1 gap-1">
          <button
            onClick={() => { switched.current = true; setActive('generic'); }}
            className={`relative px-4 py-2 rounded-lg text-sm font-medium overflow-hidden transition-colors duration-200 ${
              active === 'generic'
                ? 'text-base-content shadow-sm'
                : 'text-base-content/40 hover:text-base-content/60'
            }`}
          >
            {/* Background fill that drains over 2s on first load */}
            <span
              className={`absolute inset-0 rounded-lg bg-base-content/10 ${
                active === 'generic' && !switched.current ? 'animate-[drain_3s_linear_forwards]' : ''
              }`}
              style={active !== 'generic' ? { transform: 'scaleX(0)' } : undefined}
            />
            <span className="relative">Generic email</span>
          </button>
          <button
            onClick={() => { switched.current = true; setActive('hypema'); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-1.5 ${
              active === 'hypema'
                ? 'bg-primary/20 text-primary shadow-sm'
                : 'text-base-content/40 hover:text-base-content/60'
            }`}
          >
            <Sparkles size={14} />
            HyPeMa email
          </button>
        </div>
      </div>

      {/* Email card */}
      <div className="glass-card rounded-xl overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-base-300/20">
          <div className="flex gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-error/60" />
            <span className="w-2.5 h-2.5 rounded-full bg-warning/60" />
            <span className="w-2.5 h-2.5 rounded-full bg-success/60" />
          </div>
          <div className="flex-1 text-center">
            <span className="text-xs text-base-content/30">New Message</span>
          </div>
          <Copy size={14} className="text-base-content/20" />
        </div>

        <div className="px-5 pt-4 space-y-1.5 border-b border-base-300/10 pb-3">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-base-content/30 w-8">To:</span>
            <span className="text-base-content/60">{current.to}</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-base-content/30 w-8">Subj:</span>
            <span className={`font-medium ${active === 'hypema' ? 'text-base-content/80' : 'text-base-content/50'}`}>
              {current.subject}
            </span>
          </div>
        </div>

        <div className="px-5 py-4">
          <pre className="text-sm text-base-content/60 leading-relaxed whitespace-pre-wrap font-sans">
            {current.body}
          </pre>
        </div>

        <div className={`px-5 py-3 border-t border-base-300/10 flex items-center justify-between ${
          active === 'hypema' ? 'bg-primary/5' : 'bg-base-300/5'
        }`}>
          <div>
            <span className={`text-xs font-semibold ${active === 'hypema' ? 'text-primary' : 'text-base-content/40'}`}>
              {current.label}
            </span>
            <span className="text-xs text-base-content/25 ml-2">
              {current.sublabel}
            </span>
          </div>
          {active === 'hypema' && (
            <span className="badge badge-primary badge-xs gap-1">
              <Sparkles size={10} /> AI-researched
            </span>
          )}
        </div>
      </div>

      <style>{`
        @keyframes drain {
          from { transform: scaleX(1); transform-origin: right; }
          to { transform: scaleX(0); transform-origin: right; }
        }
      `}</style>
    </div>
  );
}
